import { z } from 'zod';

// duplicated from the owning domain package (contracts stay domain-free);
// session/application/src/enum-sync.ts asserts they stay in lockstep
export const noteStatus = ['TODO', 'DONE'] as const;
export const sessionInputMethods = ['LIVE_RECORDING', 'DICTATION'] as const;
export const noteSource = ['GENERATED', 'MANUAL'] as const;

import { chiefComplaintSchema } from './nesso-note/chief-complaint';
import { clientPresentationSchema } from './nesso-note/client-presentation';
import { interventionsSchema } from './nesso-note/interventions';
import { planSchema } from './nesso-note/plan';
import { progressSchema } from './nesso-note/progress';
import { riskSchema } from './nesso-note/risk';

export const sessionNoteTypeSchema = z.enum(['SOAP', 'NESSO']);
export type NoteType = z.infer<typeof sessionNoteTypeSchema>;

export const sessionNoteStatusSchema = z.enum(noteStatus);
export type NoteStatus = z.infer<typeof sessionNoteStatusSchema>;

export const sessionNoteSourceSchema = z.enum(noteSource);
export type NoteSource = z.infer<typeof sessionNoteSourceSchema>;

export const soapNoteContentSchema = z.object({
    subjective: z.string().trim(),
    objective: z.string().trim(),
    assessment: z.string().trim(),
    plan: z.string().trim(),
});
export type SoapNoteContent = z.infer<typeof soapNoteContentSchema>;

export const nessoNoteContentSchema = z.object({
    clientPresentation: clientPresentationSchema,
    chiefComplaint: chiefComplaintSchema,
    risk: riskSchema.nullish(),
    interventions: interventionsSchema,
    progress: progressSchema,
    plan: planSchema,
    additionalNotes: z.string().trim().optional(),
});

export const generationStatusSchema = z.enum([
    'PREPARING_TRANSCRIPT',
    'DELETING_AUDIO',
    'GENERATING_NOTE',
    'DONE',
]);
export type GenerationStatus = z.infer<typeof generationStatusSchema>;

export const sessionNoteFormatSchema = z.enum(['narrative', 'bulleted']);
export type NoteFormat = z.infer<typeof sessionNoteFormatSchema>;
export const sessionNoteLengthSchema = z.enum(['concise', 'standard']);
export type NoteLength = z.infer<typeof sessionNoteLengthSchema>;
export const sessionNoteIdentifierSchema = z.enum(['client', 'patient']);
export type NoteIdentifier = z.infer<typeof sessionNoteIdentifierSchema>;

export const formatSettingsSchema = z.object({
    format: sessionNoteFormatSchema,
    length: sessionNoteLengthSchema,
    identifier: sessionNoteIdentifierSchema,
    includeCpt90837Justification: z.boolean().optional(),
    includeDiagnosisCodes: z.boolean().optional(),
});
export type FormatSettings = z.infer<typeof formatSettingsSchema>;

export const generationSettingsSchema = formatSettingsSchema.and(
    z.object({
        type: sessionNoteTypeSchema,
        inputMethod: z.enum(sessionInputMethods),
        treatmentApproaches: z.array(z.string()),
        pronouns: z.string().default('they/them'),
    }),
);
export type GenerationSettings = z.infer<typeof generationSettingsSchema>;

export const sessionNoteContentSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('SOAP'),
        content: soapNoteContentSchema,
    }),
    z.object({
        type: z.literal('NESSO'),
        content: nessoNoteContentSchema,
    }),
]);

// The vocabulary behind SessionNote.feedbackReasons: the ids stored on a submission, and the
// labels a reader is shown. Published so consumers label ids instead of restating the list.
export const noteFeedbackReasonLabels: Record<string, string> = {
    inaccurate: 'Inaccurate information',
    irrelevant: 'Irrelevant information',
    missing: 'Missing key information',
    other: 'Other',
};
